import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  getDeveloperWithProjects,
  updateProjectForDeveloper,
  deleteProjectForDeveloper
} from '../api/developerManagementApi'

export default function AdminDeveloperDashboardView() {
  const { developerId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedProject, setSelectedProject] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editFormData, setEditFormData] = useState({})

  const { data: developerData, isLoading } = useQuery({
    queryKey: ['admin-developer-details', developerId],
    queryFn: async () => {
      const { data, error } = await getDeveloperWithProjects(developerId)
      if (error) throw error
      return data
    },
    enabled: !!developerId
  })

  const updateMutation = useMutation({
    mutationFn: ({ projectId, updates }) => updateProjectForDeveloper(projectId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-developer-details', developerId])
      setShowEditModal(false)
      setSelectedProject(null)
      alert('Project updated successfully')
    },
    onError: (error) => {
      alert(error.message || 'Failed to update project')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: deleteProjectForDeveloper,
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-developer-details', developerId])
      alert('Project deleted successfully')
    },
    onError: (error) => {
      alert(error.message || 'Failed to delete project')
    }
  })

  const handleEditProject = (project) => {
    setSelectedProject(project)
    setEditFormData({
      title: project.title,
      description: project.description || '',
      location: project.location || '',
      address: project.address || '',
      status: project.status
    })
    setShowEditModal(true)
  }

  const handleUpdateSubmit = (e) => {
    e.preventDefault()
    updateMutation.mutate({
      projectId: selectedProject.id,
      updates: editFormData
    })
  }

  const handleDeleteProject = (projectId, projectTitle) => {
    if (window.confirm(`Are you sure you want to delete "${projectTitle}"? This action cannot be undone.`)) {
      deleteMutation.mutate(projectId)
    }
  }

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      coming_soon: 'bg-blue-100 text-blue-800',
      published: 'bg-green-100 text-green-800',
      activated: 'bg-primary-100 text-primary-800',
      completed: 'bg-secondary-100 text-secondary-800'
    }
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${styles[status] || styles.draft}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-secondary-500">Loading developer details...</div>
      </div>
    )
  }

  if (!developerData || !developerData.developer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-lg text-error-600 mb-4">Developer not found</div>
          <button onClick={() => navigate('/admin')} className="btn-outline">
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    )
  }

  const developer = developerData.developer
  const projects = developerData.projects || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin" className="text-sm text-primary-600 hover:text-primary-700 mb-2 inline-block">
            ← Back to Admin Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-secondary-900">{developer.full_name}</h1>
          {developer.company_name && (
            <p className="text-lg text-secondary-600">{developer.company_name}</p>
          )}
        </div>
        <Link
          to={`/admin/developers/${developerId}/create-project`}
          className="btn-primary"
        >
          Create Project for Developer
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Developer Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-secondary-500">Email</label>
            <p className="text-sm text-secondary-900">{developer.email}</p>
          </div>
          {developer.phone && (
            <div>
              <label className="text-sm font-medium text-secondary-500">Phone</label>
              <p className="text-sm text-secondary-900">{developer.phone}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-secondary-500">Role</label>
            <p className="text-sm text-secondary-900">{developer.role}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-secondary-500">Joined</label>
            <p className="text-sm text-secondary-900">
              {new Date(developer.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h2 className="text-lg font-semibold text-secondary-900">Projects ({projects.length})</h2>
        </div>
        <div className="p-6">
          {projects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-secondary-500 mb-4">No projects yet</p>
              <Link
                to={`/admin/developers/${developerId}/create-project`}
                className="btn-primary"
              >
                Create First Project
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200">
                  {projects.map((project) => (
                    <tr key={project.id} className="hover:bg-secondary-50">
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-secondary-900">{project.title}</div>
                        {project.description && (
                          <div className="text-xs text-secondary-500 mt-1 line-clamp-2">
                            {project.description}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {project.location || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getStatusBadge(project.status)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {new Date(project.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <Link
                            to={`/projects/${project.id}`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => handleEditProject(project)}
                            className="text-secondary-600 hover:text-secondary-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProject(project.id, project.title)}
                            className="text-error-600 hover:text-error-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showEditModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900">Edit Project</h3>
            </div>
            <form onSubmit={handleUpdateSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Title <span className="text-error-600">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="input-field"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={editFormData.location}
                    onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Status
                  </label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    className="input-field"
                  >
                    <option value="draft">Draft</option>
                    <option value="coming_soon">Coming Soon</option>
                    <option value="published">Published</option>
                    <option value="activated">Activated</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  className="input-field"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedProject(null)
                  }}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="btn-primary"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
